import { useCallback, useMemo } from 'react';

import { startMeasure, useStopMeasureWhenRendered } from '@mint-lib/datadog';
import { atom, useAtom, useSetAtom } from 'jotai';

import { BuilderTableRow } from '../pages/Builder/types.js';
import { TRACKING_EVENTS } from '../types/performanceTracking.js';
import { useBuilderTableViewDataSelector } from './useGetBuilderTableViewData.js';
import { useGetMediaPlanRows } from './useGetMediaPlanRows.js';

type PerformanceTrackingState = {
  columnSchema: string | null;
  columnWidth: string | null;
  tableRows: string | null;
};

const performanceTrackingAtom = atom<PerformanceTrackingState>({
  columnSchema: null,
  columnWidth: null,
  tableRows: null,
});

const usePreparedPerformanceTrackingState = () => {
  const { data: columnSchema = null } =
    useBuilderTableViewDataSelector('columns');
  const tableRows: BuilderTableRow[] = useGetMediaPlanRows();
  const { data: columnWidths = null } =
    useBuilderTableViewDataSelector('columnWidth');

  return useMemo(() => {
    return {
      columnSchema: JSON.stringify(columnSchema),
      columnWidth: JSON.stringify(columnWidths),
      tableRows: JSON.stringify(tableRows),
    };
  }, [columnSchema, columnWidths, tableRows]);
};

export const useTrackingInitiator = () => {
  const setTrackingState = useSetAtom(performanceTrackingAtom);
  const stringifiedState = usePreparedPerformanceTrackingState();

  const saveDataBeforeMeasure = useCallback(() => {
    setTrackingState(stringifiedState);
  }, [stringifiedState]);

  const startMeasurePerformance = useCallback(
    (eventName: TRACKING_EVENTS) => {
      saveDataBeforeMeasure();
      startMeasure(eventName);
    },
    [saveDataBeforeMeasure],
  );

  return startMeasurePerformance;
};

export const useTablePerformanceTracking = (component: string) => {
  // current state
  const { columnSchema, columnWidth, tableRows } =
    usePreparedPerformanceTrackingState();

  // state at the beginning of a measure
  const [previousState] = useAtom(performanceTrackingAtom);
  const setTrackingState = useSetAtom(performanceTrackingAtom);

  const resetTrackingState = () => {
    setTrackingState({
      columnSchema: null,
      columnWidth: null,
      tableRows: null,
    });
  };

  const metaData = {
    react: {
      component,
    },
  };

  const mapEventToState = {
    [TRACKING_EVENTS.VALUE_INSERTION]: {
      currentState: null,
      previousState: null,
    },
    [TRACKING_EVENTS.TABLE_UPDATE]: {
      currentState: tableRows,
      previousState: previousState.tableRows,
    },
    [TRACKING_EVENTS.COLUMN_REORDER]: {
      currentState: columnSchema,
      previousState: previousState.columnSchema,
    },
    [TRACKING_EVENTS.COLUMN_ADDITION]: {
      currentState: columnSchema,
      previousState: previousState.columnSchema,
    },
    [TRACKING_EVENTS.COLUMN_SUBTRACTION]: {
      currentState: columnSchema,
      previousState: previousState.columnSchema,
    },
    [TRACKING_EVENTS.CHANNEL_ADDITION]: {
      currentState: tableRows,
      previousState: previousState.tableRows,
    },
    [TRACKING_EVENTS.PLATFORM_ADDITION]: {
      currentState: tableRows,
      previousState: previousState.tableRows,
    },
    [TRACKING_EVENTS.ROW_ADDITION]: {
      currentState: tableRows,
      previousState: previousState.tableRows,
    },
    [TRACKING_EVENTS.COLUMN_RESIZE]: {
      currentState: columnWidth,
      previousState: previousState.columnWidth,
    },
  };

  const shouldIgnoreNextRender = (eventName: TRACKING_EVENTS) => {
    const { currentState, previousState } = mapEventToState[eventName];
    if (!previousState) {
      return false;
    }

    const isSameLink = currentState === previousState;

    if (!isSameLink) {
      resetTrackingState();
    }
    return isSameLink;
  };

  // condition for "tableUpdate" event
  const skipTableUpdateMeasureStop = useMemo(() => {
    return shouldIgnoreNextRender(TRACKING_EVENTS.TABLE_UPDATE);
  }, [
    mapEventToState[TRACKING_EVENTS.TABLE_UPDATE].currentState,
    mapEventToState[TRACKING_EVENTS.TABLE_UPDATE].previousState,
  ]);
  // condition for "columnReorder" event
  const skipColumnReorderMeasureStop = useMemo(() => {
    return shouldIgnoreNextRender(TRACKING_EVENTS.COLUMN_REORDER);
  }, [
    mapEventToState[TRACKING_EVENTS.COLUMN_REORDER].currentState,
    mapEventToState[TRACKING_EVENTS.COLUMN_REORDER].previousState,
  ]);
  // condition for "columnAddition" event
  const skipColumnAdditionMeasureStop = useMemo(() => {
    return shouldIgnoreNextRender(TRACKING_EVENTS.COLUMN_ADDITION);
  }, [
    mapEventToState[TRACKING_EVENTS.COLUMN_ADDITION].currentState,
    mapEventToState[TRACKING_EVENTS.COLUMN_ADDITION].previousState,
  ]);
  // condition for "columnSubtraction" event
  const skipColumnSubtractionMeasureStop = useMemo(() => {
    return shouldIgnoreNextRender(TRACKING_EVENTS.COLUMN_SUBTRACTION);
  }, [
    mapEventToState[TRACKING_EVENTS.COLUMN_SUBTRACTION].currentState,
    mapEventToState[TRACKING_EVENTS.COLUMN_SUBTRACTION].previousState,
  ]);
  // condition for "channelAddition" event
  const skipChannelAdditionMeasureStop = useMemo(() => {
    return shouldIgnoreNextRender(TRACKING_EVENTS.CHANNEL_ADDITION);
  }, [
    mapEventToState[TRACKING_EVENTS.CHANNEL_ADDITION].currentState,
    mapEventToState[TRACKING_EVENTS.CHANNEL_ADDITION].previousState,
  ]);
  // condition for "platformAddition" event
  const skipPlatformAdditionMeasureStop = useMemo(() => {
    return shouldIgnoreNextRender(TRACKING_EVENTS.PLATFORM_ADDITION);
  }, [
    mapEventToState[TRACKING_EVENTS.PLATFORM_ADDITION].currentState,
    mapEventToState[TRACKING_EVENTS.PLATFORM_ADDITION].previousState,
  ]);
  // condition for "rowAddition" event
  const skipRowAdditionMeasureStop = useMemo(() => {
    return shouldIgnoreNextRender(TRACKING_EVENTS.ROW_ADDITION);
  }, [
    mapEventToState[TRACKING_EVENTS.ROW_ADDITION].currentState,
    mapEventToState[TRACKING_EVENTS.ROW_ADDITION].previousState,
  ]);
  // condition for "columnResize" event
  const skipColumnResizeMeasureStop = useMemo(() => {
    return shouldIgnoreNextRender(TRACKING_EVENTS.COLUMN_RESIZE);
  }, [
    mapEventToState[TRACKING_EVENTS.COLUMN_RESIZE].currentState,
    mapEventToState[TRACKING_EVENTS.COLUMN_RESIZE].previousState,
  ]);

  useStopMeasureWhenRendered(TRACKING_EVENTS.TABLE_UPDATE, metaData, {
    shouldIgnoreRender: skipTableUpdateMeasureStop,
  });

  useStopMeasureWhenRendered(TRACKING_EVENTS.COLUMN_REORDER, metaData, {
    shouldIgnoreRender: skipColumnReorderMeasureStop,
  });

  useStopMeasureWhenRendered(TRACKING_EVENTS.COLUMN_ADDITION, metaData, {
    shouldIgnoreRender: skipColumnAdditionMeasureStop,
  });

  useStopMeasureWhenRendered(TRACKING_EVENTS.COLUMN_SUBTRACTION, metaData, {
    shouldIgnoreRender: skipColumnSubtractionMeasureStop,
  });

  useStopMeasureWhenRendered(TRACKING_EVENTS.CHANNEL_ADDITION, metaData, {
    shouldIgnoreRender: skipChannelAdditionMeasureStop,
  });

  useStopMeasureWhenRendered(TRACKING_EVENTS.PLATFORM_ADDITION, metaData, {
    shouldIgnoreRender: skipPlatformAdditionMeasureStop,
  });

  useStopMeasureWhenRendered(TRACKING_EVENTS.ROW_ADDITION, metaData, {
    shouldIgnoreRender: skipRowAdditionMeasureStop,
  });

  useStopMeasureWhenRendered(TRACKING_EVENTS.COLUMN_RESIZE, metaData, {
    shouldIgnoreRender: skipColumnResizeMeasureStop,
  });
};
